import React from "react";
import PropTypes from "prop-types";
import Classnames from "classnames";
import * as d3 from "d3";

import Weighting from "../Weighting";
import EditableValue from "./EditableValue";
import Radio from "../Radio";
import Footnote from "../Footnote";

import Link from '../../assets/Link_icon_horiz.png';

import "./style.css";

const format = (value, multiplier = 1, precision = 0) =>
  typeof value === "number" ? (value * multiplier).toFixed(precision) : value;

const aggregate = (values, weightings) =>
  weightings == null
    ? d3.sum(values)
    : d3.sum(values, (d, i) => d * weightings[i]) / d3.sum(weightings);

const SummaryRow = props => (
  <tr
    key={`summary: ${props.groupKey}`}
    className={Classnames("summary-row", 
      props.subgroupBy != null && "group", 
      props.compare && "compare-foot")} >
    {props.tableType === "proposed" && props.compare
      ? null 
      : <td />}
    <td className="hide-sm" />
    <td>{props.subgroupBy == null ? "Total" : ""}</td>
    {props.fields.map(({ key, type, multiplier, precision, aggregation }) => (
      <td key={key} className={type}>
        {aggregation == null
          ? key === props.subgroupBy ? `${props.groupKey} Total` : ""
          : format(
              aggregate(
                props.values.map(d => d[key]),
                typeof aggregation === "string"
                  ? props.values.map(d => d[aggregation])
                  : null
              ),
              multiplier,
              precision
            )
            + (key === "expenseRatio" || key === "expenseRatioGross" ? "bps" : "")}
      </td>
    ))}
    {props.extraCells}
  </tr>
);

export default class CompositionTable extends React.PureComponent {
  static propTypes = {
    rows: PropTypes.array,
    fields: PropTypes.array,
    weightingSchemes: PropTypes.object,
    fixedWeighting: PropTypes.bool
  };

  static defaultProps = {
    rows: null,
    fields: [],
    fixedWeighting: false
  };

  render() {
    const {
      rows,
      fields,
      footnote,
      removeIndex,
      alternateFormat,
      subgroupBy,
      faded,
      split,
      performanceTitleAlt,
      width,
      wtRegion,
      TEXT,
      originalPortfolioLabel,
      proposedPortfolioLabel,
    } = this.props;

    const tableType = this.props.tableType || "";
    const performanceTitle = this.props.performanceTitle || "";

    const ttOverall = tableType === "overall";
    const fundDetails = tableType === "fundDetails";
    const ptReturns = performanceTitle === TEXT.returnsPerformanceTableLabel;
    const avAnnualTitle = ptReturns ? TEXT.averageAnnualPerformanceTableLabel : TEXT.averageAnnualVolatilityTableLabel;

    const weightingMarkup = this.weightingMarkup();

    const lenCumulative = fields.filter(o => o.group === "Cumulative").length;
    const lenAverage = fields.filter(o => o.group === "Average").length;

    const fadedByTicker = {};
    (faded || []).forEach(d => (fadedByTicker[d] = true));

    /*
      alternateFormat tables {alternateFormat={true}} can be:
      (Factor Loadings (FFFF) 
        or
        Performance (Returns/Volatility/Drawdowns) {!!performanceTitle})
      and
      (Original vs. Proposed {tableType==='overall'} 
        or 
        by Ticker)
      
      FFFF tables appear in FACTOR LOADINGS
      Performance / Original vs Proposed appears in PERFORMANCE
      Performance / by Ticker appears in COMPOSITION
    */
    let tableHeader;
    if (alternateFormat && performanceTitle) {
    //FUND STANDARDIZED PERFORMANCE
      tableHeader = (
        <thead className={"alternate-format " + tableType}>
          <tr>
            <th rowSpan="2" />
            <th
              rowSpan="2"
              colSpan={ptReturns ? 1 : 1 + lenCumulative}
              className={Classnames({"wide": !performanceTitleAlt, 
                "white-border-right": ttOverall || width < 729,
                "extra-wide": ttOverall && ptReturns,
                "xx-wide": ttOverall && !ptReturns})}
            >
              {performanceTitleAlt || performanceTitle}
              {footnote && ptReturns && !performanceTitleAlt &&
                <Footnote mark={<span className="q" />}>
                  {footnote}
                </Footnote>}
            </th>
            {ttOverall
              ? null
              : <th rowSpan="2" className="white-border-right hide-sm xx-wide">
                  Name
                </th>}
            {ptReturns && 
              <th
                rowSpan="1"
                colSpan={lenCumulative}
                className="center white-border-bottom white-border-right small">
                {TEXT.cumulativeReturnsPerformanceTableLabel
                  .replace("[[ASOFDATE]]", d3.timeFormat("%m/%d/%Y")(new Date(rows[0].asOfDate)))}
              </th>}
            <th
              rowSpan="1"
              colSpan={lenAverage}
              className={Classnames("center","white-border-bottom","small",{
                "white-border-right": ttOverall && !!performanceTitle
              })}>
              {avAnnualTitle.replace("[[ASOFDATE]]", d3.timeFormat("%m/%d/%Y")(new Date(rows[0].asOfDate)))}
            </th>
            {!!performanceTitle && !performanceTitleAlt &&
              <th rowSpan="1" colSpan="1" className="white-border-bottom"></th>}
          </tr>
          <tr>
            {fields.map(({ key, name, group }, i) => (
              <th
                key={key}
                className={Classnames("center","group",{
                  "white-border-right": !!performanceTitle 
                    && ((!performanceTitleAlt 
                      && i === lenCumulative + lenAverage - 1)
                      || i === lenCumulative - 1)
                })}
              >
                {name}
              </th>
            ))}
            {!removeIndex ? null : <th className="remove">
              <div className="action" onClick={() => removeIndex(-1)}>
                ✕
              </div>
            </th>}
          </tr>
        </thead>
      );
    } else if (alternateFormat) {
      // Factors
      tableHeader = (
        <thead className={"alternate-format " + tableType}>
          <tr>
            <th colSpan="1" /> 
            <th
              colSpan={ttOverall ? 1 : 2}
              rowSpan={ttOverall ? 2 : 1}
              className={Classnames({
                "white-border-right": true,
                "factor-xx-wide": ttOverall, 
                "portfolio-name": true,
              })}
            >
              {tableType === 'original' ? originalPortfolioLabel : tableType === 'proposed' ? proposedPortfolioLabel : 'Portfolio'}
            </th>            
            <th
              colSpan={fields.length}
              className="center white-border-bottom small"
            >
              {TEXT.factorLabel}
            </th>
          </tr>
          <tr>
            <th colSpan="1" />    
            {ttOverall ? null : <th colSpan="1">{TEXT.entryTableTickerLabel}</th>}        
            {ttOverall
              ? null
              : <th rowSpan="2" className="white-border-right factor-name hide-sm">
                  {TEXT.nameFactorLoadingsLabel}
                </th>}                
            {fields.map(({ key, name, type }) => (
              <th key={key} className={"center " + type}>{name}</th>
            ))}
            {!removeIndex ? null : <th className="remove">
              <div className="action" onClick={() => removeIndex(-1)}>
                ✕
              </div>
            </th>}
          </tr>
        </thead>
      );
    } else { 
    //FUND DETAILS
      tableHeader = (
        <thead className={Classnames({ fundDetails })}>
          <tr>
            {(tableType === "proposed" && !split)
              ? null 
              : <th />}
            <th className="ticker">{TEXT.entryTableTickerLabel}</th>
            {!fundDetails && <th className="hide-sm">{TEXT.entryTableNameLabel}</th>}
            {weightingMarkup && weightingMarkup.head}
            {fields.map(({ key, name, type, asOfDate }) => (
              <th key={key} className={"center "+type}>
                {name}
                {asOfDate && 
                  <span className="small">
                  {` (AS OF ${d3.timeFormat("%m/%d/%Y")(new Date(rows[0].asOfDate))})`}
                  </span>}
              </th>
            ))}
            {!removeIndex ? null : <th className="remove">
              <div className="action" onClick={() => removeIndex(-1)}>
                ✕
              </div>
            </th>}
          </tr>
        </thead>
      );
    }

    let tableBody;
    if (alternateFormat && performanceTitle) {
    //FUND STANDARDIZED PERFORMANCE
      tableBody = (
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.ticker+"_"+i}
              className={Classnames({
                faded: fadedByTicker[row.ticker],
                "no-border-bottom": ttOverall
              })}
            >
              <td className="index" />
              <td
                colSpan={ptReturns ? 1 : 1 + lenCumulative}
                className={"ticker alternate-format " + row.ticker}
              >
                <span>
                  {/* If the table if for portfolio performance then display the portfolio labels otherwise show ticker */}
                  {ttOverall ? row.ticker.toUpperCase() === 'ORIGINAL' ? (originalPortfolioLabel ? originalPortfolioLabel : TEXT.originalPortfolioLabel) :
                    (row.ticker.toUpperCase() === 'PROPOSED' ? (proposedPortfolioLabel ? proposedPortfolioLabel : TEXT.proposedPortfolioLabel) :
                      row.ticker.toUpperCase()) :
                    row.ticker.toUpperCase()}
                </span>
              </td>
              {!ttOverall &&
                <td className="hide-sm">
                  <span>{row.name+" (NAV)"}</span>
                  {performanceTitleAlt && wtRegion === 'US' &&
                    <span><br/>{row.name+" (MP)"}</span>}
                </td>}
              {ptReturns &&
                fields
                  .filter(o => o.group === "Cumulative")
                  .map(({ key, type }) => (
                    <td key={key} className={type + " center"}>
                      <span>{row[key+"NAV"] || "--"}</span>
                      {performanceTitleAlt && wtRegion === 'US' &&
                        <span><br/>{row[key+"MP"] || "--"}</span>}
                    </td>
                  ))}
              {fields
                .filter(o => o.group === "Average")
                .map(({ key, type }) => (
                  <td key={key} className={type + " center"}>
                    <span>{row[key+"NAV"] || "--"}</span>
                    {performanceTitleAlt && wtRegion === 'US' &&
                        <span><br/>{row[key+"MP"] || "--"}</span>}
                  </td>
                ))}
              {!!performanceTitle && !performanceTitleAlt &&
                fields
                  .filter(o => o.group === "CP")
                  .map(({ key, type }) => (
                    <td key={key} className={type + " center"}>
                      {row[key+"NAV"] || "--"}
                    </td>
                  ))}
              {!removeIndex
                ? null
                : <td className="close">
                    <div className="action" onClick={() => removeIndex(i)}>
                      ✕
                    </div>
                  </td>}
            </tr>
          ))}
        </tbody>
      );
    } else if (alternateFormat && ttOverall) {
      tableBody = (
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.ticker+"_"+i} className="no-border-bottom">
              <td className="index" />
              <td className={"ticker alternate-format " + row.ticker}>
                <span>{row.ticker.toUpperCase() === 'ORIGINAL' ? (originalPortfolioLabel ? originalPortfolioLabel: TEXT.originalPortfolioLabel)
                     : (proposedPortfolioLabel ? proposedPortfolioLabel: TEXT.proposedPortfolioLabel)}</span>
              </td>
              {fields.map(({ key, type }) => (
                <td key={key} className={type + " center"}>
                  {row[key] || "--"}
                </td>
              ))}
              {!removeIndex
                ? null
                : <td className="close">
                    <div className="action" onClick={() => removeIndex(i)}>
                      ✕
                    </div>
                  </td>}
            </tr>
          ))}
        </tbody>
      );
    } else if (alternateFormat) {
      // Factors
      tableBody = (
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.ticker+"_"+i}>
              <td className="index">{i + 1}</td>
              <td className="ticker">
                <span>{row.ticker}</span>
              </td>
              <td className="hide-sm"><span>{row.name}</span></td>
              {fields.map(({ key, type }) => (
                <td key={key} className={type + " center"}>{row[key]}</td>
              ))}
              {!removeIndex
                ? null
                : <td className="close">
                    <div className="action" onClick={() => removeIndex(i)}>
                      ✕
                    </div>
                  </td>}
            </tr>
          ))}
        </tbody>
      );
    } else {
    //FUND DETAILS
      const groups = d3.nest().key(d => d[subgroupBy]).entries(rows);

      const trueIndexByTicker = {};
      rows.forEach(({ ticker }, i) => (trueIndexByTicker[ticker] = i));

      const indexByTicker = {};
      let nextIndex = 0;
      groups.forEach(({ values }) =>
        values.forEach(({ ticker }) => (indexByTicker[ticker] = nextIndex++))
      );

      tableBody = (
        <tbody>
          {groups.map(({ key: groupKey, values }) =>
            values
              .map((row,i) => (
                <tr
                  key={row.ticker+"_"+i}
                  className={Classnames({ faded: fadedByTicker[row.ticker] })}>
                  {(tableType === "proposed" && !split)
                    ? null 
                    : fundDetails
                      ? <td className="index" />
                      : <td className="index">{groups.length === 1 && split ? i+1 : indexByTicker[row.ticker] + 1}</td>}
                  <td className="ticker">{row.ticker}</td>
                  {!fundDetails && <td className="hide-sm"><span>{row.name}</span></td>}
                  {weightingMarkup &&
                    weightingMarkup.body(row, indexByTicker[row.ticker])}
                  {fields.map(({ key, type, multiplier, precision }) => 
                    fundDetails && type === 'fd-link'
                      ? (<td key={key} className={Classnames("center", type)}>
                          <span className="fd-link-hide-pdf"
                            onClick={() => this.props.dialogLeavingWTSite(row[key])}>
                            <img className="link" src={Link} alt="Link"/>
                          </span>
                          <a className="fd-link-pdf-only" href={row[key]}>
                            <img className="link" src={Link} alt="Link"/>
                          </a>
                        </td>)
                      : (<td 
                          key={key} 
                          className={Classnames({ type, center: (fundDetails && type !== 'fd-wide') || !fundDetails})}>
                          {!row[key] || row[key] === '-'
                            ? "--"
                            : format(row[key], multiplier, precision) + 
                              ((key === "expenseRatio" || key === "expenseRatioGross") 
                                ? "bps" + (row.expenseRatioAdjusted ? "*" : "")
                                : "")}
                        </td>)
                  )}
                  {!removeIndex
                    ? null
                    : <td className="remove">
                        <div
                          className="action"
                          onClick={() =>
                            removeIndex(trueIndexByTicker[row.ticker])}
                        >
                          ✕
                        </div>
                      </td>}
                </tr>
              ))
              .concat(
                subgroupBy && split
                  ? [
                      <SummaryRow
                        fields={fields}
                        values={values}
                        groupKey={groupKey}
                        subgroupBy={subgroupBy}
                      />
                    ]
                  : []
              )
          )}
        </tbody>
      );
    }

    const tableFoot = alternateFormat || fundDetails
      ? null
      : <tfoot>
          <SummaryRow
            fields={fields}
            values={rows}
            groupKey="Portfolio"
            subgroupBy={subgroupBy}
            tableType={tableType}
            extraCells={weightingMarkup && weightingMarkup.foot}
            compare={!split}
          />
        </tfoot>;

    return (
      <div className="composition-container">
        <table className={Classnames("composition-table", tableType)}>
          {tableHeader}
          {tableBody}
          {tableFoot}
        </table>
      </div>
    );
  }

  weightingMarkup() {
    const { rows, weightingSchemes, fixedWeighting, width, TEXT } = this.props;

    const {
      setWeighting,
      setPercentageWeighting,
      unsetPercentageWeighting,
      setFixedWeighting,
    } = this.props;

    if (weightingSchemes == null) {
      return null;
    }

    const {
      weightings,
      normalized: normalizedWeightings,
      normalizedSum,
      percentage: percentageWeightings,
      preexisting: preexistingWeightings
    } = weightingSchemes;

    const weights = Object.keys(weightings).map(
      key =>
        preexistingWeightings != null
          ? preexistingWeightings[key]
          : weightings[key]
    );
    const weightingWidth = width < 729 ? (width < 440 ? 50 : 100) : 200;
    const weightingScale = d3.scaleLinear();
    weightingScale
      .domain([0, Math.max(d3.max(weights), 2 * d3.mean(weights) + 0.01)])
      .range([0, weightingWidth]);

    const formatWeightingValue = d3.format(",.2f");
    const formatWeightingPercentage = d3.format(".1f");

    return {
      head: [
        <th key={0} className="numeric-column weighting-column">{TEXT.entryTableWeightingLabel}</th>,
        <th key={1}>
          <Radio
            selected={fixedWeighting ? TEXT.entityCCYSymbol : "%"}
            values={["%", TEXT.entityCCYSymbol]}
            onChange={value => setFixedWeighting(value === TEXT.entityCCYSymbol)}
          />
        </th>
      ],

      foot: [
        <td key={0} className="numeric-column weighting-column summary-field">
          {fixedWeighting
            ? formatWeightingValue(
                rows.reduce((prev, { ticker }) => prev + weightings[ticker], 0)
              )
            : formatWeightingPercentage(
                100 *
                  rows.reduce(
                    (prev, { ticker }) => prev + normalizedWeightings[ticker],
                    0
                  )
              )}
        </td>,
        <td key={1} className="warning">
          {Math.abs(normalizedSum - 1) < 0.01
            ? null
            : `${normalizedSum < 0.99 ? "Less" : "More"} than 100% in total`}
        </td>
      ],

      body: (row, i) => [
        <td key={0} className="numeric-column weighting-column">
          {fixedWeighting
            ? <EditableValue
                value={weightings[row.ticker]}
                format={formatWeightingValue}
                onChange={value => {
                  unsetPercentageWeighting(row.ticker);
                  setWeighting(row.ticker, +value, "input");
                }}
                min={0}
                step={1}
                tabIndex={i + 1}
              />
            : <EditableValue
                value={normalizedWeightings[row.ticker] * 100}
                format={formatWeightingPercentage}
                onChange={value => {
                  setPercentageWeighting(row.ticker, value / 100);
                }}
                className={Classnames({
                  emphasis: percentageWeightings[row.ticker] != null
                })}
                min={0}
                max={100}
                step={1}
                tabIndex={rows.length + i + 1}
              />}
        </td>,
        <td key={1} className="bar-column">
          <div className="percentage-annotation">
            {fixedWeighting
              ? `${formatWeightingPercentage(normalizedWeightings[row.ticker] * 100)}%`
              : null}
          </div>
          <Weighting
            colWidth={weightingWidth}
            weighting={weightings[row.ticker]}
            previous={
              preexistingWeightings && preexistingWeightings[row.ticker]
            }
            scale={weightingScale}
            onChange={(value, method) => {
              unsetPercentageWeighting(row.ticker);
              setWeighting(row.ticker, Math.round(value * 10) / 10, method);
            }}
          />
        </td>
      ]
    };
  }
}



// WEBPACK FOOTER //
// ./src/components/CompositionTable/index.js